import type { ConsentData } from '@/core/types';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

export class GTMIntegration {
  private static instance: GTMIntegration | null = null;
  private projectId: string;
  private debugging: boolean;

  private constructor(projectId: string, debugging = false) {
    this.projectId = projectId;
    this.debugging = debugging;
  }

  public static getInstance(
    projectId: string,
    debugging = false
  ): GTMIntegration {
    if (!GTMIntegration.instance) {
      GTMIntegration.instance = new GTMIntegration(projectId, debugging);
    }
    return GTMIntegration.instance;
  }

  public initialize(): void {
    this.ensureDataLayerExists();
    this.pushInitialData();
    this.log('GTM Integration initialized');
  }

  public pushConsentUpdate(consent: ConsentData): void {
    this.ensureDataLayerExists();

    const consentObject = {
      event: 'cmp_consent_update',
      consent_data: {
        essential: consent.essential,
        analytics: consent.analytics,
        advertising: consent.advertising,
        functional: consent.functional,
        personalization: consent.personalization,
        timestamp: consent.timestamp,
        version: consent.version,
        region: consent.region,
        language: consent.language,
      },
      project_id: this.projectId,
    };

    window.dataLayer?.push(consentObject);
    this.log('Pushed consent update to dataLayer:', consentObject);
  }

  public pushConsentEvent(
    eventType:
      | 'banner_shown'
      | 'banner_hidden'
      | 'modal_shown'
      | 'modal_hidden'
      | 'consent_given'
      | 'consent_withdrawn'
      | 'consent_check'
      | 'consent_journey',
    additionalData?: Record<string, any>
  ): void {
    this.ensureDataLayerExists();

    const eventObject = {
      event: 'cmp_event',
      event_category: 'consent',
      event_action: eventType,
      event_timestamp: Date.now(),
      project_id: this.projectId,
      ...additionalData,
    };

    window.dataLayer?.push(eventObject);
    this.log(`Pushed ${eventType} event to dataLayer:`, eventObject);
  }

  public createConsentGate(
    triggerName: string,
    requiredConsent: Array<
      keyof Omit<ConsentData, 'timestamp' | 'version' | 'region' | 'language'>
    >
  ): void {
    this.ensureDataLayerExists();

    const gateObject = {
      event: 'cmp_consent_gate',
      gate_name: triggerName,
      required_consent: requiredConsent,
      project_id: this.projectId,
      timestamp: Date.now(),
    };

    window.dataLayer?.push(gateObject);
    this.log(`Created consent gate ${triggerName}:`, gateObject);
  }

  public checkConsentForTrigger(
    triggerName: string,
    currentConsent: ConsentData,
    requiredConsent: Array<
      keyof Omit<ConsentData, 'timestamp' | 'version' | 'region' | 'language'>
    >
  ): boolean {
    const hasRequiredConsent = requiredConsent.every(
      (category) => currentConsent[category] === true
    );

    this.pushConsentEvent('consent_check', {
      trigger_name: triggerName,
      required_consent: requiredConsent,
      has_consent: hasRequiredConsent,
      current_consent: {
        essential: currentConsent.essential,
        analytics: currentConsent.analytics,
        advertising: currentConsent.advertising,
        functional: currentConsent.functional,
        personalization: currentConsent.personalization,
      },
    });

    return hasRequiredConsent;
  }

  public createCustomVariable(variableName: string, value: any): void {
    this.ensureDataLayerExists();

    const variableObject = {
      [variableName]: value,
      cmp_project_id: this.projectId,
      cmp_timestamp: Date.now(),
    };

    window.dataLayer?.push(variableObject);
    this.log(`Set custom variable ${variableName}:`, variableObject);
  }

  public enhanceEcommerceTracking(consent: ConsentData): void {
    if (!consent.advertising) {
      this.createCustomVariable('cmp_enhanced_ecommerce_disabled', true);
      this.log(
        'Enhanced ecommerce tracking disabled due to advertising consent'
      );
    } else {
      this.createCustomVariable('cmp_enhanced_ecommerce_enabled', true);
      this.log('Enhanced ecommerce tracking enabled');
    }
  }

  public setupConsentModeIntegration(consent: ConsentData): void {
    this.ensureDataLayerExists();

    const consentModeData = {
      event: 'gtm_consent_update',
      gtm: {
        consent: {
          ad_storage: consent.advertising ? 'granted' : 'denied',
          analytics_storage: consent.analytics ? 'granted' : 'denied',
          functionality_storage: consent.functional ? 'granted' : 'denied',
          personalization_storage: consent.personalization
            ? 'granted'
            : 'denied',
          security_storage: 'granted',
          ad_user_data: consent.advertising ? 'granted' : 'denied',
          ad_personalization: consent.advertising ? 'granted' : 'denied',
        },
      },
      project_id: this.projectId,
    };

    window.dataLayer?.push(consentModeData);
    this.log('Updated GTM consent mode:', consentModeData);
  }

  public trackConsentJourney(
    step: string,
    metadata?: Record<string, any>
  ): void {
    this.pushConsentEvent('consent_journey', {
      journey_step: step,
      ...metadata,
    });
  }

  public generateConsentReport(): {
    dataLayerEvents: number;
    lastConsentUpdate: number | null;
    consentHistory: any[];
  } {
    const dataLayer = window.dataLayer || [];
    const consentEvents = dataLayer.filter(
      (item: any) =>
        item.event === 'cmp_consent_update' || item.event === 'cmp_event'
    );

    const lastConsentUpdate =
      consentEvents
        .filter((item: any) => item.event === 'cmp_consent_update')
        .slice(-1)[0]?.consent_data?.timestamp || null;

    return {
      dataLayerEvents: consentEvents.length,
      lastConsentUpdate,
      consentHistory: consentEvents.slice(-10), // Last 10 events
    };
  }

  public debugDataLayer(): void {
    if (!this.debugging) return;

    const dataLayer = window.dataLayer || [];
    console.group('[GTM Integration] DataLayer Debug');
    console.log('DataLayer length:', dataLayer.length);
    console.log(
      'Recent CMP events:',
      dataLayer.filter((item: any) => item.event?.startsWith('cmp_')).slice(-5)
    );
    console.groupEnd();
  }

  public waitForGTMContainer(
    containerId: string,
    timeout = 5000
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const checkContainer = () => {
        const gtmElement = document.querySelector(
          `script[src*="${containerId}"]`
        );
        const dataLayerHasGTM = window.dataLayer?.some(
          (item: any) => item['gtm.start'] || item.event === 'gtm.js'
        );

        if (gtmElement && dataLayerHasGTM) {
          this.log(`GTM container ${containerId} loaded successfully`);
          resolve(true);
          return;
        }

        if (Date.now() - startTime > timeout) {
          this.log(`GTM container ${containerId} load timeout`);
          resolve(false);
          return;
        }

        setTimeout(checkContainer, 100);
      };

      checkContainer();
    });
  }

  public injectGTMCode(containerId: string): void {
    if (!containerId || typeof window === 'undefined') return;

    this.ensureDataLayerExists();

    const gtmScript = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;

    const script = document.createElement('script');
    script.innerHTML = gtmScript;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;

    if (document.body) {
      document.body.appendChild(noscript);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(noscript);
      });
    }

    this.log(`Injected GTM container: ${containerId}`);
  }

  private ensureDataLayerExists(): void {
    if (typeof window === 'undefined') return;

    if (!window.dataLayer) {
      window.dataLayer = [];
      this.log('Created dataLayer array');
    }
  }

  private pushInitialData(): void {
    const initialData = {
      event: 'cmp_initialized',
      project_id: this.projectId,
      cmp_version: '1.0.0',
      timestamp: Date.now(),
    };

    window.dataLayer?.push(initialData);
    this.log('Pushed initial CMP data:', initialData);
  }

  private log(message: string, ...args: any[]): void {
    if (this.debugging) {
      console.log(`[GTM Integration] ${message}`, ...args);
    }
  }

  public destroy(): void {
    GTMIntegration.instance = null;
  }
}
